import ServiceClient, {
  type FetchWithRetryOptions,
} from "src/utils/extension/ServiceClient";
import {
  RateLimitGate,
  IntegrationRateLimitWaitSchedule,
} from "src/utils/extension/rateLimitGate";
import {
  getVintedCookies,
  getUserIdFromToken,
  refreshVintedSessionWithPopup,
  VintedSessionRefreshAbortedError,
} from "src/utils/extension/vinted";
import {
  isVintedRateLimitedPath,
  isVintedSessionRefreshPath,
  needsVintedSessionPopup,
  vintedSessionPopupReason,
} from "src/utils/extension/vintedSession";
import {
  createVintedRefreshRunId,
  recordVintedSessionRefreshAborted,
  recordVintedSessionRefreshSucceeded,
} from "src/utils/extension/vintedSessionRefreshEvents";
import { recordVintedPayloadDrift } from "./vintedSyncEvents";

const VintedBaseUrl = "https://www.vinted.com";

/**
 * The wallet ledger moved off `www` / `/api/v2` (the old
 * `/api/v2/wallet/invoices/{year}/{month}` now serves Vinted's HTML 404 page).
 * Every other endpoint we call still lives on {@link VintedBaseUrl}.
 */
const VintedApiBaseUrl = "https://api.www.vinted.com";

/**
 * Safety net for the fallback that pages until an empty response. Only applies
 * when Vinted omits a page count; a reported `total_pages` is still honored.
 */
const VintedWalletInvoiceMaxPages = 100;

const VintedItemPageHeaders = {
  accept: "text/html,application/xhtml+xml",
  "accept-language": "en-US",
  locale: "en-US",
};

const VintedHeaders = {
  accept: "application/json,text/plain,*/*,image/webp",
  "accept-language": "en-US",
  locale: "en-US",
};

export type VintedItemPageDetails = {
  plugins?: Array<Record<string, unknown>>;
  jsonLd?: Record<string, unknown>;
  breadcrumbs?: Array<{ title: string; url?: string }>;
};

/**
 * Drives RateLimitGate retries: HTTP 429, or conditions that need the session
 * popup (/rate-limited, /session-refresh, invalid_authentication_token).
 * Plain 401/403 without that body are not retried.
 */
export function isVintedRateLimitedResponse(res: {
  status: number;
  url?: string;
  data?: unknown;
}): boolean {
  if (res.status === 429) return true;
  return needsVintedSessionPopup(res);
}

/**
 * Usable success for the gate: HTTP 2xx and not a recovery landing page.
 * Non-2xx (429/401/403/…) are never successful; retries are decided separately
 * by {@link isVintedRateLimitedResponse}.
 */
export function isSuccessfulVintedResponse(res: {
  ok: boolean;
  url?: string;
}): boolean {
  if (!res.ok) return false;
  if (isVintedRateLimitedPath(res.url || "")) return false;
  if (isVintedSessionRefreshPath(res.url || "")) return false;
  return true;
}

function extractNextFPushStrings(html: string): string[] {
  const results: string[] = [];
  const re = /self\.__next_f\.push\(\[1,"((?:\\.|[^"])*)"\]\)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(html))) {
    try {
      results.push(JSON.parse(`"${match[1]}"`));
    } catch {
      // skip malformed flight chunks
    }
  }
  return results;
}

function sliceBalancedJson(str: string, startIdx: number): string | null {
  const open = str[startIdx];
  if (open !== "{" && open !== "[") return null;
  const close = open === "[" ? "]" : "}";

  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = startIdx; i < str.length; i++) {
    const char = str[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (char === "\\") escaped = true;
      else if (char === '"') inString = false;
      continue;
    }
    if (char === '"') {
      inString = true;
      continue;
    }
    if (char === open) depth++;
    else if (char === close) {
      depth--;
      if (depth === 0) return str.slice(startIdx, i + 1);
    }
  }

  return null;
}

function extractJsonValueFromSlice(slice: string, key: string): unknown | null {
  const marker = `"${key}":`;
  const idx = slice.indexOf(marker);
  if (idx === -1) return null;

  let start = idx + marker.length;
  while (start < slice.length && /\s/.test(slice[start])) start++;

  const jsonSlice = sliceBalancedJson(slice, start);
  if (!jsonSlice) return null;

  try {
    return JSON.parse(jsonSlice);
  } catch {
    return null;
  }
}

function extractItemPagePlugins(
  pushes: string[]
): Array<Record<string, unknown>> | undefined {
  for (const push of pushes) {
    if (!push.includes('"plugins":[{"name":"item_status"')) continue;
    const idx = push.indexOf('"plugins":');
    if (idx === -1) continue;
    const plugins = extractJsonValueFromSlice(push.slice(idx), "plugins");
    if (Array.isArray(plugins)) return plugins;
  }
  return undefined;
}

function extractItemPageBreadcrumbsFromHtml(html: string) {
  const results: Array<{ title: string; url?: string }> = [];
  const re =
    /<li class="breadcrumbs__item"><a href="([^"]*)"[^>]*><span itemProp="title">([^<]*)<\/span>/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(html))) {
    results.push({
      url: match[1],
      title: match[2].replace(/&amp;/g, "&"),
    });
  }
  return results;
}

function extractItemPageJsonLdFromHtml(
  html: string
): Record<string, unknown> | undefined {
  const re = /<script type="application\/ld\+json">([\s\S]*?)<\/script>/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(html))) {
    try {
      const parsed = JSON.parse(match[1]);
      if (parsed?.["@type"] === "Product") return parsed;
    } catch {
      // try next script tag
    }
  }
  return undefined;
}

/** Extract structured Next.js payloads from a Vinted item HTML page. */
export function extractVintedItemPagePayloads(
  html: string
): VintedItemPageDetails {
  const pushes = extractNextFPushStrings(html);
  return {
    plugins: extractItemPagePlugins(pushes),
    jsonLd: extractItemPageJsonLdFromHtml(html),
    breadcrumbs: extractItemPageBreadcrumbsFromHtml(html),
  };
}

export class NotLoggedError extends Error {
  constructor() {
    super("You must be logged in Vinted to perform the sync process.");
  }
}

/**
 * The listing page does not exist for this item any more (or is not public).
 * Vinted keeps returning these rows from the wardrobe/my_orders APIs, so this
 * is an expected, item-level condition — callers should keep the row and
 * continue instead of aborting the whole import.
 */
export class VintedItemUnavailableError extends Error {
  status: number;
  url: string;
  responseUrl?: string;
  body: string;

  constructor({
    status,
    url,
    responseUrl,
    body,
  }: {
    status: number;
    url: string;
    responseUrl?: string;
    body?: unknown;
  }) {
    super(`(vinted) getItemPage Failed (status ${status})`);
    this.name = "VintedItemUnavailableError";
    this.status = status;
    this.url = url;
    this.responseUrl = responseUrl;
    this.body = typeof body === "string" ? body : body ? String(body) : "";
  }
}

/** Statuses Vinted answers with when the listing page is gone or not public. */
const ItemPageUnavailableStatuses = [404, 410];

/**
 * Transient statuses worth retrying before failing the import. 429 (and the
 * session-popup responses) are handled by the rate-limit gate on top of these.
 */
const ItemPageRetryStatuses = [408, 500, 502, 503, 504, 524];

export function isRetryableItemPageResponse(res: {
  status: number;
  url?: string;
  data?: unknown;
}): boolean {
  if (isVintedRateLimitedResponse(res)) return true;
  return ItemPageRetryStatuses.includes(res.status);
}

class VintedClient extends ServiceClient {
  private rateLimitGate = new RateLimitGate();

  private async refreshSessionViaPopup(lastRes?: {
    status: number;
    url?: string;
    data?: unknown;
  }) {
    const reason = lastRes
      ? vintedSessionPopupReason(lastRes)
      : "homepage-session-refresh";
    try {
      await refreshVintedSessionWithPopup(this.extensionId, { reason });
    } catch (e) {
      if (e instanceof VintedSessionRefreshAbortedError) {
        throw new NotLoggedError();
      }
      throw e;
    }
  }

  /**
   * `isRateLimited` widens what the shared gate retries. Only the item page
   * overrides it (to also retry 5xx/524); every other endpoint keeps the
   * default, which retries 429 / session-popup responses only. There is no
   * second retry loop in this method — the base `ServiceClient.fetchWithRetry`
   * is shadowed, so non-2xx retries happen once, inside the gate.
   */
  async fetchWithRetry(
    url: string,
    opts: RequestInit = {},
    {
      retries = IntegrationRateLimitWaitSchedule.length,
      isOk = isSuccessfulVintedResponse,
      isRateLimited = isVintedRateLimitedResponse,
    }: FetchWithRetryOptions & {
      isRateLimited?: (res: {
        status: number;
        url?: string;
        data?: unknown;
      }) => boolean;
    } = {}
  ): Promise<Awaited<ReturnType<typeof this.fetch>>> {
    return await this.rateLimitGate.fetchWithRateLimitGate(
      (u, o) => this.fetch(u, o),
      url,
      opts,
      {
        retries,
        waitSchedule: IntegrationRateLimitWaitSchedule,
        isOk,
        isRateLimited,
        beforeRetry: async (lastRes) => {
          if (lastRes && needsVintedSessionPopup(lastRes)) {
            await this.refreshSessionViaPopup(lastRes);
          }
        },
        label: "vinted",
      }
    );
  }

  private async request(
    operation: string,
    url: string,
    opts: RequestInit = {}
  ) {
    const res = await this.fetchWithRetry(url, opts);

    if (isVintedRateLimitedResponse(res)) {
      // Session popup already ran during RateLimitGate retries; still unhealthy.
      if (needsVintedSessionPopup(res)) {
        throw new NotLoggedError();
      }
      console.error(`(vinted) ${operation} still rate limited after retries`, {
        url,
        responseUrl: res.url,
        status: res.status,
      });
      throw new Error(
        `(vinted) ${operation} rate limited (status ${res.status})`
      );
    }

    if (res.status === 401 || res.status === 403) {
      throw new NotLoggedError();
    }

    if (!res.ok) {
      console.error(`(vinted) ${operation} failed`, {
        url,
        responseUrl: res.url,
        status: res.status,
        body: res.data,
      });
      throw new Error(`(vinted) ${operation} Failed (status ${res.status})`);
    }

    return res.data;
  }

  async getUserIdFromCookie(): Promise<string> {
    const cookies = await getVintedCookies(this.extensionId);
    for (const name of ["refresh_token_web", "access_token_web"]) {
      const token = cookies.find((c) => c.name === name)?.value;
      if (token) {
        const userId = getUserIdFromToken(token);
        if (userId) return userId;
      }
    }
    throw new NotLoggedError();
  }

  /**
   * Homepage warm-up. Session-refresh / rate-limited landings wait (same
   * schedule as 429), open the popup, then retry within RateLimitGate.
   */
  async refreshSession() {
    const startedAt = Date.now();
    const refreshRunId = createVintedRefreshRunId();
    const res = await this.fetchWithRetry(VintedBaseUrl, {
      method: "GET",
      credentials: "include",
      headers: VintedItemPageHeaders,
    });

    console.warn("(vinted) refreshSession homepage result", {
      status: res.status,
      responseUrl: res.url,
      isSessionRefresh: isVintedSessionRefreshPath(res.url || ""),
      isRateLimitedPath: isVintedRateLimitedPath(res.url || ""),
    });

    if (!isSuccessfulVintedResponse(res)) {
      recordVintedSessionRefreshAborted({
        refreshRunId,
        reason: "homepage-still-unhealthy",
        durationMs: Date.now() - startedAt,
        message: `Homepage warm-up failed (status ${res.status})`,
      });
      throw new NotLoggedError();
    }

    recordVintedSessionRefreshSucceeded({
      refreshRunId,
      viaPopup: false,
      durationMs: Date.now() - startedAt,
    });
    return res;
  }

  /**
   * Cheap authenticated call used to prove cookies work before persisting them.
   */
  async assertAuthenticatedSession() {
    await this.getMyOrders({ page: 1, perPage: 1 });
  }

  async getTransactionDetail(orderId: string | number) {
    const url = `${VintedBaseUrl}/api/v2/transactions/${orderId}`;
    return await this.request("getTransactionDetail", url, {
      headers: VintedHeaders,
      method: "GET",
    });
  }

  /**
   * Resolve a message-thread id (UUID or canonical) to `conversation.id`.
   * UUID inbox links redirect here; wallet Sold `entity_id` is often UUID while
   * older sales store num11/`500*` as `Sales.transaction_id`.
   */
  async getConversation(threadId: string | number) {
    const url = `${VintedBaseUrl}/api/v2/conversations/${threadId}`;
    return await this.request("getConversation", url, {
      headers: VintedHeaders,
      method: "GET",
    });
  }

  async getMyOrders({ page = 1, perPage = 100 } = {}) {
    const url = `${VintedBaseUrl}/api/v2/my_orders?type=sold&status=all&per_page=${perPage}&page=${page}`;
    return await this.request("getMyOrders", url, {
      headers: VintedHeaders,
      method: "GET",
    });
  }

  async *getMyOrdersFull() {
    for (let page = 1; ; page++) {
      const res = await this.getMyOrders({ page });
      const orders = res?.my_orders || [];
      yield orders;

      const pagination = res?.pagination;
      if (!pagination || page >= (pagination.total_pages || 1)) break;
    }
  }

  async getWardrobeItems({
    userId,
    page = 1,
    perPage = 100,
  }: {
    userId: string;
    page?: number;
    perPage?: number;
  }) {
    const url = `${VintedBaseUrl}/api/v2/wardrobe/${userId}/items?page=${page}&per_page=${perPage}&order=newest_first`;
    return await this.request("getWardrobeItems", url, {
      headers: VintedHeaders,
      method: "GET",
    });
  }

  async *getWardrobeItemsFull(
    userId: string,
    { perPage = 100 }: { perPage?: number } = {}
  ) {
    for (let page = 1; ; page++) {
      const res = await this.getWardrobeItems({ userId, page, perPage });
      const items = res?.items || [];
      yield items;

      const pagination = res?.pagination;
      if (!pagination || page >= (pagination.total_pages || 1)) break;
    }
  }

  async getItemDetails(itemId: string | number) {
    const url = `${VintedBaseUrl}/api/v2/items/${itemId}/details`;
    return await this.request("getItemDetails", url, {
      headers: VintedHeaders,
      method: "GET",
    });
  }

  async getItemPage(itemUrl: string): Promise<string> {
    // Retries transient failures (5xx/524/429) inside the gate, then hands back
    // the last response. 404/410 are not retried — the listing page is gone.
    const res = await this.fetchWithRetry(
      itemUrl,
      { headers: VintedItemPageHeaders, method: "GET" },
      { isRateLimited: isRetryableItemPageResponse }
    );

    if (isVintedRateLimitedResponse(res)) {
      if (needsVintedSessionPopup(res)) throw new NotLoggedError();
      console.error("(vinted) getItemPage still rate limited after retries", {
        url: itemUrl,
        responseUrl: res.url,
        status: res.status,
      });
      throw new Error(
        `(vinted) getItemPage rate limited (status ${res.status})`
      );
    }

    if (res.status === 401 || res.status === 403) throw new NotLoggedError();

    if (ItemPageUnavailableStatuses.includes(res.status)) {
      throw new VintedItemUnavailableError({
        status: res.status,
        url: itemUrl,
        responseUrl: res.url,
        body: res.data,
      });
    }

    if (!res.ok) {
      console.error("(vinted) getItemPage failed", {
        url: itemUrl,
        responseUrl: res.url,
        status: res.status,
        body: res.data,
      });
      throw new Error(`(vinted) getItemPage Failed (status ${res.status})`);
    }

    return String(res.data ?? "");
  }

  async getWalletInvoices({
    year,
    month,
    page = 1,
  }: {
    year: number;
    month: number;
    page?: number;
  }) {
    const url = `${VintedApiBaseUrl}/wallet/history/month_summary?year=${year}&month=${month}&page=${page}`;
    return await this.request("getWalletInvoices", url, {
      headers: VintedHeaders,
      method: "GET",
    });
  }

  async *getWalletInvoicesFull({
    year,
    month,
  }: {
    year: number;
    month: number;
  }) {
    let reportedMissingLines = false;
    let reportedUnknownPagination = false;

    for (let page = 1; ; page++) {
      const res = await this.getWalletInvoices({ year, month, page });
      if (res && !res.lines && !reportedMissingLines) {
        reportedMissingLines = true;
        recordVintedPayloadDrift({
          endpoint: "getWalletInvoices",
          issue: "missing-lines",
          keys: Object.keys(res),
          year,
          month,
          page,
        });
      }

      const lines = res?.lines || [];
      yield lines;

      const totalPages = res?.pagination?.total_pages;
      if (totalPages != null) {
        if (page >= totalPages) break;
        continue;
      }

      // No page count, so we can't trust a single page to be the whole month.
      // Keep asking until Vinted hands back an empty page instead.
      if (res?.pagination && !reportedUnknownPagination) {
        reportedUnknownPagination = true;
        recordVintedPayloadDrift({
          endpoint: "getWalletInvoices",
          issue: "unknown-pagination",
          keys: Object.keys(res.pagination),
          year,
          month,
          page,
        });
      }

      if (!lines.length) break;

      if (page >= VintedWalletInvoiceMaxPages) {
        recordVintedPayloadDrift({
          endpoint: "getWalletInvoices",
          issue: "page-cap-reached",
          keys: res?.pagination ? Object.keys(res.pagination) : [],
          year,
          month,
          page,
        });
        break;
      }
    }
  }
}

export default VintedClient;
